import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useInfoWidgetService } from '../../../services/info-widget-service';
import { QueryKey } from '../../../services/query-keys';
import { Route } from '../../../routes';
import Table from '../../ui/Table/Table';
import Button, { EButtonColor, EButtonSize } from '../../ui/Button/Button';
import { IInfoWidget, typeOptions } from '../../../shared/IInfoWidget';
import Spinner from '../../ui/Spinner/Spinner';
import ErrorsAlert from '../../ErrorsAlert/ErrorsAlert';
import { ErrorResponse } from '../../../custom-fetch';

const typeLabels = Object.fromEntries(typeOptions.map(opt => [opt.value, opt.label]));

const InfoWidgetsList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { fetchInfoWidgets, saveInfoWidgets, deleteInfoWidget } = useInfoWidgetService();

  const {
    data: initialWidgets,
    isPending,
    isError,
    error,
  } = useQuery<IInfoWidget[], ErrorResponse>({
    queryKey: [QueryKey.infoWidgets],
    queryFn: fetchInfoWidgets,
  });

  const [widgets, setWidgets] = useState<IInfoWidget[]>([]);

  useEffect(() => {
    if (initialWidgets) {
      setWidgets([...initialWidgets].sort((a, b) => a.ordering - b.ordering));
    }
  }, [initialWidgets]);

  const saveMutation = useMutation({
    mutationFn: saveInfoWidgets,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.infoWidgets] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteInfoWidget,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.infoWidgets] });
    },
  });

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(widgets);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({
      ...item,
      ordering: index,
    }));

    setWidgets(updatedItems);
    saveMutation.mutate(updatedItems);
  };

  if (isPending) return <Spinner />;
  if (isError) return <ErrorsAlert errors={error.messages} />;

  const handleEdit = (id: string) => {
    navigate(Route.infoWidget(id));
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this widget?")) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="info-widgets">
        {(provided) => (
          <Table hover>
            <thead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th>Type</th>
                <th>Content Preview</th>
                <th>Visible</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody {...provided.droppableProps} ref={provided.innerRef}>
              {widgets.map((widget, index) => (
                <Draggable key={widget.id} draggableId={widget.id!} index={index}>
                  {(provided, snapshot) => (
                    <tr
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                        background: snapshot.isDragging ? '#f0f0f0' : 'transparent',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleEdit(widget.id!)}
                    >
                      <td {...provided.dragHandleProps} onClick={(e) => e.stopPropagation()}>
                        <span style={{ fontSize: '20px', color: '#999' }}>☰</span>
                      </td>
                      <td>{typeLabels[widget.type]}</td>
                      <td>
                        <div
                          style={{
                            maxWidth: '300px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          dangerouslySetInnerHTML={{ __html: widget.content }}
                        />
                      </td>
                      <td>{widget.isVisible ? 'Yes' : 'No'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                          <Button
                            color={EButtonColor.PRIMARY}
                            size={EButtonSize.SMALL}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(widget.id!);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            color={EButtonColor.DANGER}
                            size={EButtonSize.SMALL}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(widget.id!);
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              {widgets.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center' }}>No widgets found.</td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default InfoWidgetsList;
